import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";
import { Radar } from "react-chartjs-2";
import { Slider } from "../Slider/Slider";
import { useState, useEffect } from "react";
import classNames from "classnames";

interface GraphRatioSelectProps {
  labels: string[];
  value: number[];
  readOnly?: boolean;
  onChange?: (value: number[]) => void;
}

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

export const GraphRatioSelect = ({
  labels,
  value,
  onChange,
  readOnly,
}: GraphRatioSelectProps) => {
  const [chartData, setChartData] = useState<number[]>([]);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  // Update chart data when value changes
  useEffect(() => {
    setChartData(value || Array(labels?.length).fill(0));
  }, [value, labels]);

  // Color values for the chart
  const primaryColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--primary-500')
    .trim() || '#7b4228';
  const primaryColorLight = getComputedStyle(document.documentElement)
    .getPropertyValue('--primary-200')
    .trim() || '#f2e2cf';

  return (
    <div className="font-sans rounded-lg border border-primary-100 dark:border-primary-800 bg-base-50 dark:bg-content-800 shadow-sm transition-colors overflow-hidden">
      <div className="px-4 py-3 border-b border-primary-100 dark:border-primary-800">
        <div className="text-primary-900 dark:text-primary-100 font-medium text-sm">
          Flavor Profile
        </div>
      </div>

      <div className="p-4">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Chart section */}
          <div className="order-2 lg:order-1 lg:col-span-5 flex items-center justify-center">
            <div className="relative h-64 w-64 lg:h-full lg:w-full max-w-full">
              <Radar
                data={{
                  labels: labels,
                  datasets: [
                    {
                      backgroundColor: `${primaryColor}40`,
                      borderColor: primaryColor,
                      borderWidth: 2,
                      pointBackgroundColor: primaryColor,
                      pointBorderColor: '#fff',
                      pointStyle: "circle",
                      pointRadius: 4,
                      pointHoverRadius: 6,
                      pointHoverBackgroundColor: primaryColor,
                      pointHoverBorderColor: '#fff',
                      data: chartData,
                    },
                  ],
                }}
                options={{
                  maintainAspectRatio: false,
                  responsive: true,
                  plugins: {
                    legend: { display: false },
                    tooltip: {
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      titleColor: '#334155',
                      bodyColor: '#334155',
                      borderColor: primaryColorLight,
                      borderWidth: 1,
                      padding: 8,
                      displayColors: false,
                      callbacks: {
                        title: (items) => {
                          return labels[items[0].dataIndex];
                        },
                        label: (context) => {
                          return `Value: ${context.raw}`;
                        },
                      }
                    },
                  },
                  scales: {
                    r: {
                      angleLines: {
                        display: true,
                        color: `${primaryColorLight}80`,
                      },
                      grid: {
                        color: `${primaryColorLight}80`,
                      },
                      ticks: {
                        display: false,
                        stepSize: 1,
                        backdropColor: 'transparent',
                      },
                      pointLabels: {
                        color: 'var(--primary-800, #334155)',
                        font: {
                          size: 10,
                          family: "'Inter', sans-serif",
                        },
                        padding: 8,
                      },
                      min: 0,
                      max: 5,
                      beginAtZero: true,
                    },
                  },
                }}
              />
            </div>
          </div>

          {/* Sliders section */}
          <div className="order-1 lg:order-2 lg:col-span-7 space-y-4">
            {labels?.map((label, i) => {
              const currentValue = value?.[i] || 0;
              const isActive = activeIndex === i;

              return (
                <div
                  key={label}
                  className={classNames(
                    "p-2 rounded-md transition-colors",
                    isActive ? "bg-primary-50/50 dark:bg-primary-900/20" : ""
                  )}
                  onMouseEnter={() => setActiveIndex(i)}
                  onMouseLeave={() => setActiveIndex(null)}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-primary-800 dark:text-primary-200 font-medium text-sm">
                      {label}
                    </div>
                    <div className="text-primary-600 dark:text-primary-400 font-mono text-xs">
                      {currentValue}/5
                    </div>
                  </div>

                  <Slider
                    min={0}
                    max={5}
                    readOnly={readOnly}
                    id={`${label}-slider`}
                    name={label}
                    value={currentValue}
                    onChange={(e) => {
                      const updatedValues = value?.slice() || Array(labels?.length).fill(0);
                      updatedValues[i] = parseInt(e.target.value);
                      onChange && onChange(updatedValues);
                    }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
